import { useMemo, useState } from 'react'
import type { AssignableRole, RoleGrant } from '../../lib/roles'
import type { OrgUnitOption } from '../../lib/admin'
import { scopeIds, scopeOptions, type BuPlace } from '../../lib/where'
import { InfoIcon } from '../icons'
import { ScopePicker } from './ScopePicker'

/**
 * One role, on one line (11 Sep, David).
 *
 * ── WHY IT SHRANK ───────────────────────────────────────────────────────────
 *
 * Three roles were taking most of the panel's height: a heading, a sentence of
 * description, a caution, a list of grants, a scope picker and a Grant button
 * each — plus a paragraph explaining that Employee is not on the list. That is
 * a lot of screen for the thing an administrator changes least, sitting above
 * the facts they change constantly. The facts moved up; this got tighter.
 *
 * ── THE DESCRIPTION IS HIDDEN, NOT DELETED ──────────────────────────────────
 *
 * lib/roles.ts argues at length that a key tells an admin nothing — that
 * somebody choosing who moderates ~900 colleagues' speech should be reading a
 * sentence rather than a snake_case identifier. That still holds, so the
 * sentences are one press away and marked with the caution's own colour when
 * there is one, rather than being dropped.
 *
 * ── A DISCLOSURE, NOT A HOVER TOOLTIP ───────────────────────────────────────
 *
 * A hover tooltip does not exist on a tablet, and this app has already reversed
 * one decision for exactly that reason: 046's ctrl-click multi-select became
 * the Picker the following day because there is no modifier key on a tablet and
 * `--tap-min: 44px` is in this stylesheet precisely because the hub is used on
 * them. A description reachable only by hovering would be a description the
 * people most likely to need it cannot read.
 *
 * So it is a real toggle with `aria-expanded`, and `title` carries the same
 * text for a mouse — the hover is the bonus, never the mechanism.
 *
 * ── THE TOGGLE IS TENANT-WIDE; SCOPE IS ITS OWN ROW ─────────────────────────
 *
 * 024's scoped grants are real and currently held by nobody, and a single
 * on/off cannot express "comms_admin, but only for Chile". Collapsing them out
 * of the UI would leave the mechanism reachable by nothing, which is the shape
 * that left `wiki.curate` orphaned from 011 until 022 and `content.moderate` on
 * no role at all. So the toggle means WHOLE COMPANY, any scoped grants appear
 * beneath as their own removable rows, and the scope picker only renders for a
 * role that is scopable and only when asked for.
 */
export function RoleRow({
  role,
  held,
  orgUnits,
  membershipActive,
  lockedLastAdmin,
  lockedReason = null,
  allowWide = true,
  canRemoveScope,
  scopeLabel,
  typical = [],
  busy,
  onGrant,
  onGrantMany,
  onRevoke,
  places = [],
}: {
  role: AssignableRole
  /** Every grant of THIS role — tenant-wide and scoped. */
  held: RoleGrant[]
  orgUnits: OrgUnitOption[]
  membershipActive: boolean
  /** The last tenant_admin's whole-company grant cannot be removed. */
  lockedLastAdmin: boolean
  /**
   * 137. Why the caller may not change this row at all — an HR administrator
   * looking at the two privileged roles, or at somebody who holds one. Said
   * on the row rather than only disabling it: a greyed button with no reason
   * is how somebody decides the screen is broken.
   */
  lockedReason?: string | null
  /**
   * 153. False for a regional HR Administrator on a scopable role: the
   * whole-company grant is group HR's, both ways. The row says so instead of
   * offering a button the server refuses.
   */
  allowWide?: boolean
  /**
   * 153. Whether the caller may remove the grant for this unit — a regional
   * holder only inside their region. Absent means every scoped row.
   */
  canRemoveScope?: (scopeId: string) => boolean
  /** 153. The scope picker's button, when "one part of the company" is not the right words. */
  scopeLabel?: string
  /**
   * 154. The role's confirmed typical holders, in words: who it is usually
   * for and why. The same sentences a suggestion carries, so somebody granting
   * by hand reads what the Suggestions tab would have said.
   */
  typical?: { name: string; why: string }[]
  /** The token currently in flight, or null. */
  busy: string | null
  onGrant: (scopeId: string | null) => void
  /**
   * 159. Several scopes in one press — "Canada and BU 51". Absent, each unit
   * goes through onGrant in turn.
   */
  onGrantMany?: (scopeIds: string[]) => void
  onRevoke: (scopeId: string | null) => void
  /** 159. Each business unit's country, so the scope picker can offer a country. */
  places?: BuPlace[]
}) {
  const [open, setOpen] = useState(false)
  const [addingScope, setAddingScope] = useState(false)
  // 159. The Where tree's choices: a country, entities, business units.
  const [scope, setScope] = useState<string[]>([])
  const available = useMemo(
    () => orgUnits.filter((o) => !held.some((g) => g.scopeId === o.orgUnitId)),
    [orgUnits, held],
  )
  const scopeTree = useMemo(() => scopeOptions(available, places).tree, [available, places])
  const chosenIds = scopeIds(scope, scopeTree).filter((id) => available.some((o) => o.orgUnitId === id))

  const heldWide = held.some((g) => !g.scopeId)
  const scoped = held.filter((g) => g.scopeId)
  const token = `${role.key}`
  const working = busy !== null
  const locked = lockedReason !== null

  return (
    <li className={`rolerow${heldWide ? ' rolerow--held' : ''}`}>
      <div className="rolerow__line">
        <span className="rolerow__name">{role.name}</span>

        <button
          type="button"
          className="rolerow__info"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          title={role.what}
        >
          <InfoIcon size={15} />
          <span className="visually-hidden">What {role.name} can do</span>
        </button>

        {/* Whole-company grant. Revoking this deliberately leaves scoped grants
            standing — revoke_role matches the org unit with `is not distinct
            from` — which is why they are listed separately below. */}
        {!allowWide ? (
          heldWide ? (
            <span className="chip chip--hint rolerow__act">Whole company</span>
          ) : null
        ) : heldWide ? (
          <button
            type="button"
            className="btn btn--quiet btn--xs rolerow__act"
            disabled={working || lockedLastAdmin || locked}
            onClick={() => onRevoke(null)}
          >
            {busy === `${token}:revoke:all` ? '…' : 'Remove'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--xs rolerow__act"
            disabled={working || !membershipActive || locked}
            onClick={() => onGrant(null)}
          >
            {busy === `${token}:grant:all` ? '…' : 'Grant'}
          </button>
        )}
      </div>

      {open && (
        <p className="rolerow__what">
          {role.what}
          {role.caution && <em className="rolerow__caution">{role.caution}</em>}
        </p>
      )}

      {open && typical.length > 0 && (
        <ul className="rolerow__typical" aria-label={`Who ${role.name} is usually for`}>
          {typical.map((t) => (
            <li key={t.name}>
              <strong>Usually for {t.name}.</strong> {t.why}
            </li>
          ))}
        </ul>
      )}

      {locked && lockedReason && <p className="rolerow__locked">{lockedReason}</p>}

      {!allowWide && !locked && (
        <p className="rolerow__locked">
          {heldWide
            ? 'Held for the whole company. A group HR Administrator can change that grant.'
            : 'You can grant this for a part of your region. The whole-company grant is a group HR Administrator’s.'}
        </p>
      )}

      {lockedLastAdmin && (
        <p className="rolerow__locked">
          Last administrator — appoint another before removing this one.
        </p>
      )}

      {scoped.length > 0 && (
        <ul className="rolerow__scopes">
          {scoped.map((g) => (
            <li key={g.scopeId}>
              <span className="chip chip--scoped">{g.scopeName ?? 'Somewhere'}</span>
              <button
                type="button"
                className="btn btn--quiet btn--xs"
                disabled={working || locked || (canRemoveScope ? !canRemoveScope(g.scopeId!) : false)}
                title={canRemoveScope && !canRemoveScope(g.scopeId!) ? 'Outside your region' : undefined}
                onClick={() => onRevoke(g.scopeId)}
              >
                {busy === `${token}:revoke:${g.scopeId}` ? '…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {role.scopable && membershipActive && !locked && orgUnits.length > 0 && (
        <div className="rolerow__scopeadd">
          {!addingScope ? (
            <button
              type="button"
              className="btn btn--quiet btn--xs"
              disabled={working}
              onClick={() => setAddingScope(true)}
            >
              {scopeLabel ?? 'Grant for one part of the company'}
            </button>
          ) : (
            <>
              <div className="rolerow__scopepick">
                <ScopePicker
                  roleName={role.name}
                  units={available}
                  places={places}
                  selected={scope}
                  onChange={setScope}
                />
              </div>
              <button
                type="button"
                className="btn btn--primary btn--xs"
                disabled={working || chosenIds.length === 0}
                onClick={() => {
                  if (onGrantMany) onGrantMany(chosenIds)
                  else for (const id of chosenIds) onGrant(id)
                  setScope([])
                  setAddingScope(false)
                }}
              >
                {chosenIds.length > 1 ? `Grant (${chosenIds.length})` : 'Grant'}
              </button>
              <button
                type="button"
                className="btn btn--quiet btn--xs"
                disabled={working}
                onClick={() => {
                  setAddingScope(false)
                  setScope([])
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}
